import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/shared/navbar/Navbar";
import { AiBrowser } from "@/v2/assets/icons/AiBrowser";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";
import { Comment01Icon } from "@/v2/assets/icons/Comment01Icon";
import { Rotate3d } from "@/v2/assets/icons/3dRotate";
import { SidebarRight } from "@/v2/assets/icons/SidebarRight";
import { Slack } from "@/v2/assets/icons/Slack";
import { SimulatorChannel } from "@/api/simulator/simulator.model";

interface RegressionBreadcrumbsProps {
  agentId?: string;
  agentName?: string;
  agentChannel?: SimulatorChannel;
  regressionSetName?: string;
  regressionSetId?: string;
  testRunId?: string;
}

export function RegressionBreadcrumbs({
  agentId,
  agentName,
  agentChannel,
  regressionSetName,
  regressionSetId,
  testRunId,
}: RegressionBreadcrumbsProps) {
  const navigate = useNavigate();

  const getChannelIconWithContainer = useCallback((channel: SimulatorChannel) => {
    const getIcon = () => {
      switch (channel) {
        case "autonomous_ticket_event":
          return <Ticket03Icon size={16} fill="blue" />;
        case "autonomous_quackchat":
          return <Comment01Icon size={16} fill="orange" />;
        case "copilot":
          return <SidebarRight size={16} fill="teal" />;
        case "slackbot":
          return <Slack size={16} fill="purple" />;
        case "knowledge_hub":
          return <Rotate3d size={16} fill="cyan" />;
        default:
          return null;
      }
    };

    const getBackgroundColor = () => {
      switch (channel) {
        case "autonomous_ticket_event":
          return "#EDF6FF";
        case "autonomous_quackchat":
          return "rgba(255, 145, 1, 0.165)";
        case "copilot":
          return "rgba(2, 192, 150, 0.095)";
        case "slackbot":
          return "#FCEFFC";
        case "knowledge_hub":
          return "rgba(1, 204, 255, 0.114)";
        default:
          return "transparent";
      }
    };

    return (
      <div
        style={{
          width: "24px",
          height: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: getBackgroundColor(),
          borderRadius: "4px",
        }}
      >
        {getIcon()}
      </div>
    );
  }, []);

  const items = [];

  if (agentId && agentName && agentChannel) {
    items.push({
      title: agentName,
      svg: getChannelIconWithContainer(agentChannel),
      onClick: () => navigate(`/automation/${agentId}`),
    });
  }

  if (agentId) {
    items.push({
      title: "Accuracy Test",
      onClick: () => navigate(`/training-center/${agentId}`),
    });
  }

  if (regressionSetName && regressionSetId && agentId) {
    items.push({
      title: regressionSetName,
      onClick: () =>
        navigate(`/training-center/${agentId}/regression-sets/${regressionSetId}`),
    });
  }

  if (testRunId && regressionSetId && agentId) {
    items.push({
      title: `Test Run: ${testRunId.length > 8 ? testRunId.slice(0, 8) + "..." : testRunId}`,
      onClick: () =>
        navigate(
          `/training-center/${agentId}/regression-sets/${regressionSetId}/tests/${testRunId}`
        ),
    });
  }

  return (
    <div className="flex items-center p-3 border-b">
      <Navbar items={items} />
    </div>
  );
}

